import { useEffect, useRef, useState } from 'react';

const CARD_STYLE = {
  input: { color: '#0f0f0f', fontSize: '17px' },
  '.input-container': { borderColor: '#d4d4d8', borderRadius: '6px' },
  '.input-container.is-focus': { borderColor: '#dc2626' },
  '.input-container.is-error': { borderColor: '#dc2626' },
  '.message-text': { color: '#dc2626' },
  '.message-icon': { color: '#dc2626' },
};

const safeDestroy = async (card) => {
  try {
    await card.destroy();
  } catch (e) {
    console.warn('[square] card destroy failed', e);
  }
};

const describeError = (e) => {
  const msg = e?.message || (Array.isArray(e?.errors) && e.errors[0]?.message) || '';
  return msg
    ? `Could not load the secure card form: ${msg}`
    : 'Could not load the secure card form. Please refresh and try again.';
};

// Attaches the Square card form to containerRef once `payments` is ready.
export const useSquareCard = (payments, containerRef) => {
  const [card, setCard] = useState(null);
  const [attaching, setAttaching] = useState(true);
  const [error, setError] = useState(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!payments || !container) return undefined;
    let cancelled = false;

    (async () => {
      try {
        // Clear any stale iframe left by React StrictMode double-invocation.
        container.replaceChildren();
        if (instanceRef.current) {
          await safeDestroy(instanceRef.current);
          instanceRef.current = null;
        }
        const c = await payments.card({ style: CARD_STYLE });
        if (cancelled) { await safeDestroy(c); return; }
        await c.attach(container);
        if (cancelled) { await safeDestroy(c); return; }
        instanceRef.current = c;
        setCard(c);
      } catch (e) {
        if (!cancelled) setError(describeError(e));
      } finally {
        if (!cancelled) setAttaching(false);
      }
    })();

    return () => {
      cancelled = true;
      if (instanceRef.current) {
        safeDestroy(instanceRef.current);
        instanceRef.current = null;
      }
    };
  }, [payments, containerRef]);

  return { card, attaching, error };
};
